/**
 * useStopOwenAudioOnRouteChange
 * ================================
 *
 * Hook global (à monter UNE seule fois dans App.js) qui garantit l'arrêt
 * de toute voix Owen lors d'un changement de page React Router.
 *
 * Trigger :
 *   • Changement de `useLocation().pathname` (navigation interne, retour
 *     navigateur, redirect programmatique)
 *   • `document.visibilitychange` → onglet caché : pause uniquement
 *     (pas de stopAll, pour permettre au user de reprendre via les
 *     contrôles UI existants au retour sur l'onglet)
 *
 * Note : la fermeture d'onglet/navigateur tue le process et donc l'audio
 * naturellement — `beforeunload` n'est pas nécessaire (pas garanti mobile).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import OwenAudioBus from '../services/owenAudioBus';

export default function useStopOwenAudioOnRouteChange() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Stop audio à chaque changement de pathname (mais pas au premier mount)
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      OwenAudioBus.stopAll(`route_change ${prevPathRef.current} -> ${location.pathname}`);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Pause (pas stop) quand l'onglet devient caché → user peut reprendre
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        OwenAudioBus.pauseAll('visibility_hidden');
      }
      // visible : NE PAS auto-resume ; les contrôles UI gèrent.
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
}
